import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import api from "@/lib/api";
import { CheckCircle2, Send, ArrowRight, Stethoscope, Activity, Pill, UserPlus, ClipboardList, HeartPulse } from "lucide-react";

const professions = [
  { value: "doctor", label: "Doctor", icon: Stethoscope },
  { value: "nurse", label: "Nurse", icon: HeartPulse },
  { value: "physiotherapist", label: "Physiotherapist", icon: Activity },
  { value: "dietician", label: "Dietician", icon: Pill },
  { value: "medical_technologist", label: "Medical Technologist", icon: ClipboardList },
  { value: "ayah", label: "Ayah / Attendant", icon: UserPlus },
];

export default function ForPersonnel() {
  const [form, setForm] = useState({
    full_name: "", profession: "doctor", specialisation: "", registration_number: "",
    experience_years: "", email: "", phone: "", city: "", state: "",
    available_for: "both", message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.experience_years = payload.experience_years ? Number(payload.experience_years) : null;
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      const res = await api.post("/partnership/personnel", payload);
      toast.success(res.data?.message || "Submitted!");
      setForm({ full_name: "", profession: "doctor", specialisation: "", registration_number: "", experience_years: "", email: "", phone: "", city: "", state: "", available_for: "both", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <PageHero
        title="For Healthcare Personnel"
        subtitle="Get bookings directly from patients, accept what fits your schedule and receive payments through the platform — with the credibility of a verified profile."
      />

      <Section eyebrow="Who can join" title="We onboard six categories of personnel" center>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {professions.map((p, i) => (
            <div key={i} className="hc-card p-5 text-center">
              <p.icon className="w-7 h-7 mx-auto text-[#3780E0]" />
              <div className="mt-2 font-semibold text-[#0F2A4A] text-sm">{p.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="light" eyebrow="How it works" title="From sign-up to your first booking">
        <ol className="grid md:grid-cols-3 gap-5">
          {[
            { n: "1", t: "Apply &amp; submit documents", d: "Provide medical registration / professional credentials, ID and bank details." },
            { n: "2", t: "We verify", d: "Our team checks your registration with the relevant council before activating your account." },
            { n: "3", t: "Receive bookings", d: "Accept the bookings that suit your schedule. Reject any without penalty (patients are refunded in 7 working days)." },
          ].map((s, i) => (
            <li key={i} className="hc-card p-6 bg-white">
              <div className="w-9 h-9 rounded-full bg-[#3780E0] text-white font-bold flex items-center justify-center">{s.n}</div>
              <div className="mt-3 font-bold text-[#0F2A4A]" dangerouslySetInnerHTML={{ __html: s.t }} />
              <div className="mt-1 text-sm text-slate-600">{s.d}</div>
            </li>
          ))}
        </ol>
      </Section>

      <Section eyebrow="Get listed" title="Apply to join HomeCure">
        <form onSubmit={submit} className="hc-card p-6 md:p-8 max-w-3xl mx-auto" data-testid="personnel-inquiry-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="hc-label">Full name *</label><input required value={form.full_name} onChange={set("full_name")} className="hc-input" data-testid="pers-name" /></div>
            <div>
              <label className="hc-label">Profession *</label>
              <select value={form.profession} onChange={set("profession")} className="hc-input" data-testid="pers-profession">
                {professions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div><label className="hc-label">Specialisation</label><input value={form.specialisation} onChange={set("specialisation")} className="hc-input" /></div>
            <div><label className="hc-label">Registration / Council number</label><input value={form.registration_number} onChange={set("registration_number")} className="hc-input" /></div>
            <div><label className="hc-label">Experience (years)</label><input type="number" min="0" value={form.experience_years} onChange={set("experience_years")} className="hc-input" /></div>
            <div><label className="hc-label">Email *</label><input required type="email" value={form.email} onChange={set("email")} className="hc-input" data-testid="pers-email" /></div>
            <div><label className="hc-label">Phone *</label><input required value={form.phone} onChange={set("phone")} className="hc-input" data-testid="pers-phone" /></div>
            <div><label className="hc-label">City *</label><input required value={form.city} onChange={set("city")} className="hc-input" data-testid="pers-city" /></div>
            <div><label className="hc-label">State</label><input value={form.state} onChange={set("state")} className="hc-input" /></div>
            <div>
              <label className="hc-label">Available for</label>
              <select value={form.available_for} onChange={set("available_for")} className="hc-input">
                <option value="home">Home healthcare</option>
                <option value="hospital">Under a hospital</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>
          <div className="mt-4"><label className="hc-label">Anything else?</label><textarea rows={4} value={form.message} onChange={set("message")} className="hc-input" /></div>
          <button type="submit" disabled={submitting} className="hc-btn-primary mt-5 disabled:opacity-60" data-testid="pers-submit">
            <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </Section>

      <Section bg="primary" center>
        <h2 className="text-white text-3xl md:text-4xl font-extrabold">Have questions about joining?</h2>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="bg-white text-[#2563B3] hover:bg-slate-100 font-bold px-6 py-3 rounded-lg inline-flex items-center gap-2">Contact us <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </Section>
    </>
  );
}
